import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNotification } from '../../../hooks/useNotification';
import { logout } from '../../auth-container/reducer/authSlice';
import { Box } from '@mui/material';
import { UtmInfluencerMenuIcon } from '../../../icons';
import { hasSectionAccess } from '../../../config/rolePermissions';

const Sidebar = ({ isSidebarOpen, onClose }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const dispatch = useDispatch();
  const role = useSelector((state) => state.auth.role);
  const { showSuccessNotification } = useNotification();

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const isActiveLink = (linkHref) => window.location.pathname === linkHref;

  const handleLogout = () => {
    dispatch(logout());
    showSuccessNotification('Logout successfully');
  };

  // Add/remove 'p-0' class when Chat Messenger is active
  useEffect(() => {
    const mainBody = document.querySelector('.dashboard-main-body');
    if (!mainBody) return;

    if (window.location.pathname === '/chat') {
      mainBody.classList.add('p-0');
    } else {
      mainBody.classList.remove('p-0');
    }

    // Optional: also update dynamically when user clicks sidebar links
    const handleRouteChange = () => {
      if (window.location.pathname === '/chat') {
        mainBody.classList.add('p-0');
      } else {
        mainBody.classList.remove('p-0');
      }
    };

    window.addEventListener('popstate', handleRouteChange);
    window.addEventListener('click', handleRouteChange);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('click', handleRouteChange);
    };
  }, []);

  const menuItems = [
    { label: 'Dashboard', href: '/', icon: 'solar:home-smile-angle-outline', submenu: [] },
    { label: 'Channel Attribution', href: '/channel-attribution', icon: 'carbon:chart-network', submenu: [] },
    { label: 'Daily Actives', href: '/daily-actives', icon: 'tabler:activity-heartbeat', submenu: [] },
    { label: 'Engagement', href: '/engagement', icon: 'tabler:message-heart', submenu: [] },
    { label: 'Signup Report', href: '/signup-report', icon: 'fluent-mdl2:report-document', submenu: [] },
    { label: 'Subscription Report', href: '/subscription-report', icon: 'material-symbols:paid-outline', submenu: [] },
    { label: 'Profile Activations', href: '/profile-activations', icon: 'tabler:user-check', submenu: [] },
    { label: 'Location Insights', href: '/location-insights', icon: 'weui:location-outlined', submenu: [] },
    { label: 'User Management', href: '/user-management', icon: 'bx:user', submenu: [] },
    { label: 'Bot Management', href: '/bot-management', icon: 'lucide:bot', submenu: [] },
    { label: 'Chat Messenger', href: '/chat', icon: 'quill:chat', submenu: [] },
    { label: 'Deleted Accounts', href: '/deleted-accounts', icon: 'bx:user', submenu: [] },
    // { label: 'Analytics and Insights', href: '/analytics', icon: 'carbon:analytics', submenu: [] },
    // { label: 'Financials', href: '/financials', icon: 'streamline:dollar-coin', submenu: [] },
    // { label: 'Subscription', href: '/subscription', icon: 'material-symbols:card-membership-outline', submenu: [] },
    { label: 'Verification', href: '/verification', icon: 'material-symbols:verified-outline-rounded', submenu: [] },
    // { label: 'Marketing and Email', href: '/emails', icon: 'nimbus:marketing', submenu: [] },
    // { label: 'Reports', href: '/reports', icon: 'fluent-mdl2:report-document', submenu: [] },
    { label: 'Query Ticket Management', href: '/QueryTicketManagement', icon: 'mage:message-question-mark', submenu: [] },
    { label: 'Payment Analytics', href: '/payment-analytics', icon: <UtmInfluencerMenuIcon />, submenu: [] },
    { label: 'Email Analytics', href: '/email-analytics', icon: 'mdi:email-outline', submenu: [] },
    // { label: 'Expense', href: '/expense', icon: 'la:wallet', submenu: [] },
    // { label: 'Google Analytics', href: '/google-analytics', icon: 'carbon:analytics', submenu: [] },
    { label: 'Pop-up Notifications', href: '/popups', icon: 'material-symbols:campaign-outline', submenu: [] },
    { label: 'UTM Campaign', href: '/utm-campaigns', icon: 'carbon:analytics', submenu: [] },
    { label: 'Bot Coverage', href: '/bot-coverage', icon: 'tabler:map-search', submenu: [] },
    { label: 'AI Shortlist', href: '/ai-shortlist', icon: 'tabler:sparkles', submenu: [] },
  ].filter((item) => hasSectionAccess(role, item.href));

  return (
    <aside className={`sidebar ${isSidebarOpen ? 'active' : ''}`}>
      <button type="button" className="sidebar-close-btn" onClick={onClose}>
        <iconify-icon icon="radix-icons:cross-2" />
      </button>
      <div>
        <button
          type="button"
          onClick={(e) => e.preventDefault()}
          className="sidebar-logo w-100 justify-content-center align-items-center px-0"
        >
          <Box component="img" src="https://www.favordating.com/assets/images/favor-logo.svg" alt="" className='light-logo' />
          <Box component="img" src="https://www.favordating.com/favicon-new.png" alt="site logo" className="logo-icon" sx={{ width: 30, height: 30 }} />
        </button>
      </div>
      <div className="sidebar-menu-area">
        <ul className="sidebar-menu" id="sidebar-menu">
          {menuItems.map((item, index) => (
            <li
              key={index}
              className={`${openDropdown === index ? 'dropdown-open' : ''}`}
            >
              <a
                href={item.href}
                onClick={(e) => {
                  if (item.submenu.length) {
                    e.preventDefault();
                    toggleDropdown(index);
                  } else if (item.onClick) {
                    e.preventDefault();
                    item.onClick();
                  }
                }}
                className={`gap-10 ${isActiveLink(item.href) ? 'active-page' : ''}`}
              >
                {/* <iconify-icon
                  icon={item.icon}
                  className="menu-icon me-12"
                  width="24"
                  height="24"
                /> */}
                {typeof item.icon === "string" ? (
                  <iconify-icon
                    icon={item.icon}
                    className="menu-icon me-12"
                    width="24"
                    height="24"
                  />
                ) : (
                  <Box className="menu-icon me-0" sx={{ display: "flex" }}>
                    {item.icon}
                  </Box>
                )}
                <span>{item.label}</span>
              </a>

              {item.submenu.length > 0 && (
                <ul
                  className={`sidebar-submenu ${openDropdown === index ? 'd-block' : ''}`}
                >
                  {item.submenu.map((subitem, subindex) => (
                    <li key={subindex}>
                      <a href={subitem.href}>
                        <i className="ri-circle-fill circle-icon text-primary-600 w-auto" />
                        {subitem.label}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;
